import { computed } from 'vue';
import {
	CampaignImportEligibilityResponse, CampaignManagementAPIClient, SearchCampaign
} from '@studyportals/campaign-management-api-interface';
import { CampaignImportDetails } from '@/models/campaign-import.interface';
import { ModalErrorStates } from '@/models/modal/modal.enum';
import { Routes } from '@/models/router/router.interface';
import { NotificationStatuses } from '@/models/notifications.interface';
import WsManager from '@/infrastructure/ws-manager';
import cookieHandler from '@/infrastructure/cookie-handler';
import rollbarOperator from '@/infrastructure/rollbar-operator';
import campaignPresenter from '@/presenters/campaign-presenter';
import store from '@/store';

class ImportCampaignConfigurationPresenter {
	private _client: CampaignManagementAPIClient | null = null;
	private readonly tokenId: string;

	private destinationCampaignId = computed((): number => {
		const router = store.getters.router();
		return router.props.id;
	});

	private get client(): CampaignManagementAPIClient {
		if (!this._client) {
			this._client = new CampaignManagementAPIClient(
				webpackDefinitions.CAMPAIGN_MANAGEMENT_API_URL,
				WsManager.getBus()
			);
		}

		return this._client;
	}

	constructor() {
		this.tokenId = cookieHandler.retrieveTokenIdCookie();
	}

	public async searchCampaigns(term: string): Promise<SearchCampaign[]> {
		try {
			return await this.client.searchCampaigns(term, this.tokenId);
		} catch (error) {
			rollbarOperator.triggerError(error as Error);
			store.mutations.setModalErrorState(ModalErrorStates.SEARCH_CAMPAIGNS_FOR_IMPORT);
			return [];
		}
	}

	public async validateImportEligibility(sourceCampaignId: number): Promise<CampaignImportEligibilityResponse | null> {
		try {
			return await this.client.validateImportEligibility(this.destinationCampaignId.value, sourceCampaignId, this.tokenId);
		} catch (error) {
			rollbarOperator.triggerError(error as Error);
			store.mutations.setModalErrorState(ModalErrorStates.VALIDATE_IMPORT_ELIGIBILITY);
			return null;
		}
	}

	public async importCampaignConfigurations(sourceCampaignId: number, details: CampaignImportDetails): Promise<boolean> {
		try {
			await this.client.importCampaignConfigurations(this.destinationCampaignId.value, sourceCampaignId, this.tokenId);
		} catch (error) {
			rollbarOperator.triggerError(error as Error);
			store.mutations.setModalErrorState(ModalErrorStates.IMPORT_CAMPAIGN_CONFIGURATION);
			return false;
		}

		store.mutations.setCampaignImportBannerDetails(details);
		store.mutations.setCurrentRoute(Routes.ENTITIES_AND_GEOTARGETING, this.destinationCampaignId.value);

		// The import just changed linked organisations, attached entities, and geotargeting on the backend -
		// refresh everything that reflects them, then re-baseline what counts as "original" data, the same
		// way a regular save does (see CampaignPresenter.saveCampaignDetails / updateCampaignDataAfterSuccessfulSave).
		//
		// SAVING_IN_PROGRESS must be held for the whole span: extractCampaignChanges() short-circuits to []
		// while it's set, which is the only thing preventing a reactive read of isSaveEnabled from running
		// mid-refresh - the three refreshes land at different times, so without this guard a computed that
		// reads store state after e.g. availableEntities updates but before linkedOrganisations does (or vice
		// versa) diffs a genuinely inconsistent in-between state against the still-stale baseline, and
		// ChangesExtractor.checkForConflictingChanges throws. Awaiting before re-baselining is necessary but
		// not sufficient on its own - this guard is what actually closes the race.
		store.mutations.setNotificationStatus(NotificationStatuses.SAVING_IN_PROGRESS);
		await Promise.all([
			campaignPresenter.prepareCampaignDetails(),
			campaignPresenter.retrieveCampaignListingEntities(),
			campaignPresenter.updateCampaignReadinessInfo()
		]);
		campaignPresenter.updateOriginalCampaignData();
		store.mutations.setNotificationStatus(NotificationStatuses.SAVING_SUCCESS);

		return true;
	}
}

export default new ImportCampaignConfigurationPresenter();
